/**
 * Daily Goals section - self-loading with its own data fetching
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { Progress } from 'reactstrap';
import type { ProgressProps } from 'reactstrap';
import { useLocalized } from '../../../Hooks/useLocalized';
import { usePointsOverview } from '../Hooks/useMotivationData';
import { Theme } from '../../../Theme/Theme';
import { Translation } from '../../../Components/Translation';
import type { LocalizedValue } from '../../../Model/LocalizedValue';

const Section = styled.section``;

const SectionHeader = styled.div`
  font-weight: 700;
  font-size: 24px;
  line-height: 1.3;
  margin-bottom: 24px;
  color: ${Theme.Colors.textColor};
`;

const StatsCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 992px) {
    padding: 20px;
  }
`;

const DailyGoalsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const PointRuleItem = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  transition: all 0.2s ease;
`;

const RuleRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #5d6268;
  font-weight: 500;
`;

const RulePoints = styled.span`
  font-weight: 600;
`;

const ProgressContainerRule = styled.div`
  background-color: ${Theme.Colors.lightestGrey};
  border-radius: 16px;
  height: 10px;
  margin-top: 10px;
  width: 100%;
`;

interface RuleProgressBarProps extends ProgressProps {
  barColor?: string;
}

const ProgressBarRule = styled(Progress, {
  shouldForwardProp: (prop) => !['barColor'].includes(prop),
})<RuleProgressBarProps>`
  height: 100%;
  .progress-bar {
    background-color: ${(props) => props.barColor || Theme.Colors.primary};
    transition: width 0.3s ease;
  }
`;

const APP_ACTION_LINK_MAP: Record<string, string> = {
  'https://coach.kinastic.com/explore-tab/mental': '/mental-health',
  'https://coach.kinastic.com/explore-tab/nutrition': '/nutrition',
  'https://coach.kinastic.com/explore-tab/academy': '/academy',
  'https://coach.kinastic.com/log-activity': '/move',
  'https://coach.kinastic.com/feed': '/academy/0bfd3bb9-21a3-49aa-bbce-13e1bb39d3c8',
};

type DailyRule = {
  id: string;
  name: LocalizedValue[];
  completed: boolean;
  canBeCompleted?: boolean;
  collectedPointsToday?: number;
  collectedExperiencePoints?: number;
  totalPossiblePointsToday?: number;
  totalExperiencePoints?: number;
  appActionLink?: string;
};

const RuleItem: React.FC<{ rule: DailyRule; onClick?: () => void }> = ({ rule, onClick }) => {
  const ruleName = useLocalized(rule.name);
  const collectedPoints = rule.collectedPointsToday ?? rule.collectedExperiencePoints ?? 0;
  const totalPoints = rule.totalPossiblePointsToday ?? rule.totalExperiencePoints ?? 0;
  const percent = totalPoints > 0 ? (collectedPoints / totalPoints) * 100 : 0;

  return (
    <PointRuleItem onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <RuleRow>
        <span>{ruleName}</span>
        <RulePoints>
          {collectedPoints}/{totalPoints}
        </RulePoints>
      </RuleRow>
      <ProgressContainerRule>
        <ProgressBarRule
          value={percent}
          max="100"
          barColor={rule.completed ? '#10B981' : Theme.Colors.primary}
        />
      </ProgressContainerRule>
    </PointRuleItem>
  );
};

export const DailyGoalsSection: React.FC = observer(() => {
  const navigate = useNavigate();
  const { data: pointsData } = usePointsOverview();

  const dailyRules = pointsData?.dailyRules.filter((r) => r.completed || r.canBeCompleted) ?? [];

  if (dailyRules.length === 0) {
    return null;
  }

  const totalPoints = pointsData?.collectedPoints ?? 0;

  return (
    <Section>
      <SectionHeader>
        <Translation
          i18nKey="motivation.dailyGoals"
          defaultValue={`<span style="color: #00dda2">Already</span> {{points}} today`}
          points={totalPoints}
        />
      </SectionHeader>
      <StatsCard>
        <DailyGoalsGrid>
          {dailyRules.map((rule) => {
            const ruleLink = rule.appActionLink ? APP_ACTION_LINK_MAP[rule.appActionLink] : undefined;
            const clickable = !!ruleLink && !rule.completed;
            return (
              <RuleItem
                key={rule.id}
                rule={rule}
                onClick={clickable ? () => navigate(ruleLink!) : undefined}
              />
            );
          })}
        </DailyGoalsGrid>
      </StatsCard>
    </Section>
  );
});
